<template>
  <div class="nosto-horizontal-filter">
    <div class="nosto-horizontal-filter__inner">
      <!-- Top row: facets, sort by, item count -->
      <div class="nosto-horizontal-filter__top-row">
        <div class="nosto-horizontal-filter__item-count">
          <span
            class="nosto-horizontal-filter__item-count-text text-label-s"
            v-html="itemCountLabel"
          />
        </div>

        <div class="nosto-horizontal-filter__facets-wrap text-heading-2xs text-heading-2xs-desktop">
          <div
            v-for="facet of getFilters"
            :key="facet.id"
            ref="facetAccordions"
            class="nosto-filter__facet-accordion text-heading-2xs text-heading-2xs-desktop"
            :class="[
              {'nosto-filter__facet-accordion--active': isFacetActive(facet),
               'nosto-filter__facet-accordion--has-selection' : getFacetSelectedCount(facet) > 0
              },
            ]"
          >
            <nosto-facet
              v-if="facet.max || facet.data.length"
              ref="facetComponents"
              :choices="getChoices"
              :facet="facet"
              :is-control-active-prop="activeFacetId === facet.id"
              @choices-updated="choicesUpdated"
              @facets-ready="updateChoicesOnLoad"
              @update:is-control-active-prop="handleFacetActiveChange(facet.id, $event)"
            />
          </div>
        </div>

        <div class="nosto-horizontal-filter__sort-by text-heading-13xs text-heading-xs-desktop">
          <div
            ref="sortDropdown"
            class="
              nosto-horizontal-filter__sort-by-button
              text-heading-2xs
              text-heading-2xs-desktop
            "
          >
            <button
              type="button"
              class="nosto-horizontal-filter__sort-trigger"
              @click="toggleSortDropdown"
            >
              <span
                class="nosto-horizontal-filter__sort-by-label"
                v-text="sortByLabel"
              />
              <icon-chevron-down
                :class="{
                  'nosto-horizontal-filter__sort-icon--open': isSortDropdownOpen
                }"
              />
            </button>

            <div
              v-if="isSortDropdownOpen"
              class="nosto-horizontal-filter__sort-dropdown"
            >
              <button
                v-for="sort of getActiveOptions"
                :key="sort.id"
                type="button"
                class="nosto-horizontal-filter__sort-option"
                :class="{
                  'nosto-horizontal-filter__sort-option--selected': sortOption.id === sort.id
                }"
                @click="handleSortByClick(sort)"
              >
                <span
                  v-text="sort.label"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="activeChoices.length"
        class="nosto-horizontal-filter__bottom-row"
      >
        <span
          class="nosto-horizontal-filter__filter-label text-heading-2xs-desktop"
          v-text="$string('collection.filters')"
        />

        <div class="nosto-horizontal-filter__active-values-wrap">
          <nosto-active-values
            :active-choices="activeChoices"
            :grouped-choices="groupedChoices"
            :sort-by-options="sortByOptions"
            @choices-updated="choicesUpdated"
          />

          <div
            v-if="getChoices !== ''"
            class="nosto-horizontal-filter__button-wrap"
          >
            <btn
              class="nosto-horizontal-filter__clear-all ext-heading-2xs text-heading-2xs-desktop"
              :label="$string('collection.clear_all_filters')"
              :show-icon="false"
              @click.prevent="clearChoices"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Nosto horizontal filter (nosto-horizontal-filter)
 * -----------------------------------------------------------------------------
 * Horizontal filter bar with active filter tags for product grid.
 *
 * @component NostoHorizontalFilter
 * @description Desktop-only horizontal filter bar that displays filter facets
 * @param {Object} sortByOptions - Options for sorting.
 * @param {Number} total - Total number of items in the collection.
 * @param {Boolean} nostoReady - True if Nosto is ready.
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import NostoFacet from '~async/nosto-drawer/nosto-facet'
import NostoActiveValues from '~async/nosto-grid/nosto-active-values'
import IconChevronDown from '~icons/direction/chevron/down.svg'
import Btn from '~global/btn/btn'

export default {
  name: 'NostoHorizontalFilter',

  components: {
    NostoActiveValues,
    NostoFacet,
    IconChevronDown,
    Btn,
  },

  props: {
    total: {
      type: Number,
      default: 0,
    },
    sortByOptions: {
      type: Object,
      default: () => ({}),
    },
    nostoReady: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'choices-updated': (payload) => typeof payload === 'object',
    'clear-choices': () => true,
    'facets-ready': () => true,
  },

  data() {
    return {
      activeFacet: null,
      // Removed dataChoices - using Vuex's getChoices getter
      activeSort: null,
      choicesArray: [],
      sortOption: {
        id: '',
        label: '',
      },
      isSortDropdownOpen: false,
      activeFacetElement: null,
      activeFacetId: null,
      sortOptions: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState('main-collection', {
      groupedChoices: (state) => state.groupedChoices,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getChoices: 'main-collection/getChoices',
      getFilters: 'main-collection/getFilters',
    }),

    /**
     * Compute active options by filtering 'sort by' props based on those
     * enabled in section settings.
     * @returns {Object}
     */
    getActiveOptions() {
      return Object.values(this.sortByOptions).filter((object) => {
        return object.value === true
      })
    },

    /**
     * Compute item count label.
     * @returns {String}
     */
    itemCountLabel() {
      const count = this.nostoReady
        ? this.total
        : '<span></span>'

      return this.$string('collection.items', {
        pluralise: this.total,
        replace: { count },
      })
    },

    /**
     * Compute sort by label.
     * @returns {String}
     */
    sortByLabel() {
      // Using getChoices instead of dataChoices
      return this.getChoices.includes('sort')
        ? `${this.$string('collection.sort_by')} ${this.sortOption.label}`
        : this.$string('collection.sort_by')
    },

    /**
     * Gets array of current active choices.
     * - Excludes pagination.
     * @returns {Array}
     */
    activeChoices() {
      return this.getChoices.split('&').filter((choice) => {
        return choice !== '' && !choice.includes('page=')
      }) || []
    },
  },

  watch: {

    /**
     * Watch getChoices to handle sort select reset
     */
    getChoices(choices) {
      // Reset sort by select value when not sorting
      if (!choices.includes('sort=')) {
        if (this.$refs.sortBy) {
          this.$refs.sortBy.selectedIndex = 0
        }
      }
    },
  },

  beforeMount() {
    window.removeEventListener('resize', this.applyFacetOffsets)
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('click', this.handleFacetClickOutside)
  },

  mounted() {
    this.applyFacetOffsets()
    window.addEventListener('resize', this.applyFacetOffsets)
    this.processGroupedChoices(this.getChoices)
    document.addEventListener('click', this.handleFacetClickOutside)
    this.sortOptions = this.getActiveOptions.map(option => ({
      ...option,
      selected: false,
    }))
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      updateChoices: 'main-collection/updateChoices',
      updateFilters: 'main-collection/updateFilters',
      processGroupedChoices: 'main-collection/processGroupedChoices',
    }),

    /**
     * Remove active class on all facets.
     * - Closes accordions.
     */
    removeControlsActiveClass() {
      if (!this.$refs.facetComponents) {
        return
      }
      const components = Array.isArray(this.$refs.facetComponents)
        ? this.$refs.facetComponents
        : [this.$refs.facetComponents]
      components.forEach(component => {
        if (component.$el) {
          // Remove 'is-active' class from the root element
          component.$el.classList.remove('is-active')
        }
      })
    },

    /**
     * Clear choices and close drawer on 'clear all' click.
     */
    clearChoices() {
      this.updateChoices('')
      this.removeControlsActiveClass()
    },

    /**
     * Callback after user choices emitted from facets to drawer.
     * @param {Object} response - Response object with choices update.
     */
    choicesUpdated(response) {
      const { requestParam, action } = response
      const requestValue = decodeURI(requestParam.split('=')[1])
      let requestKey = decodeURI(requestParam.split('=')[0])
      if (requestKey.includes('&')) {
        requestKey = requestKey.slice(1)
      }

      const params = new URLSearchParams(this.getChoices)
      params.delete('page')
      const values = params.getAll(requestKey).filter((value) => value !== requestValue)

      switch (action) {
        case 'add':
          params.append(requestKey, requestValue)
          break

        case 'update':
          params.set(requestKey, requestValue)
          break

        case 'remove':
          params.delete(requestKey)

          if (values.length > 0) {
            values.forEach((value) => {
              params.append(requestKey, value)
            })
          }

          break
      }

      const newChoices = params.toString()
        .replaceAll('+', ' ')
        .replace('%5B%5D', '[]')
        .replace('%5b%5d', '[]')
        .replace('%253A', '%3A')
        .replace('%253a', '%3A')

      this.processGroupedChoices(newChoices)
    },

    /**
     * Update facets on load.
     */
    updateChoicesOnLoad() {
      const facetValues = []
      if (this.$refs.facetComponents) {
        const components = Array.isArray(this.$refs.facetComponents)
          ? this.$refs.facetComponents
          : [this.$refs.facetComponents]

        components.forEach(component => {
          if (component.$el) {
            const values = [...component.$el.querySelectorAll('[js-nosto-facet="valueMobile"]')]
            facetValues.push(...values)
          }
        })
      }

      const choicesArray = this.getChoices.split('&').filter((choice) => choice !== '')

      facetValues.forEach((element) => element.classList.remove(this.$class('active')))

      choicesArray.forEach((choice) => {
        const value = choice.split('=')[1]
        const decoded = decodeURIComponent(value)
        const activeValue = facetValues.find((element) => element.id === decoded)

        if (!activeValue) {
          return
        }

        activeValue.classList.add(this.$class('active'))
      })

      this.processGroupedChoices(this.getChoices)
    },

    /**
     * Update sort options on select change.
     * @param {Event} event - Change event.
     */
    handleSortByChange(event) {
      if (!this.nostoReady) {
        return
      }

      // Extract needed values from event
      let matchValue = event.currentTarget.value
      let action = 'add'

      if (event.currentTarget.classList.contains('nosto-filter__sort-by-desktop--option')) {
        matchValue = event.currentTarget.id

        // Check if already selected (using data instead of DOM state)
        const isCurrentlyActive = this.sortOption.id === matchValue

        if (isCurrentlyActive) {
          action = 'remove'
          // Clear selection in data
          this.sortOptions = this.getActiveOptions.map(option => ({
            ...option,
            selected: false,
          }))
        } else {
          // Set selection in data
          this.sortOptions = this.getActiveOptions.map(option => ({
            ...option,
            selected: option.id === matchValue,
          }))
        }
      }

      // Find the selected option
      const option = Object.values(this.sortByOptions).find((object) => {
        return object.id === matchValue
      })

      if (!option) {
        return
      }

      const { id, label } = option

      const data = {
        requestParam: `&sort=${id}`,
        action,
      }

      this.$emit('choices-updated', data)

      // Update sort option in data (Vue will handle the UI update)
      this.sortOption = action === 'remove' ? { id: '', label: '' } : { id, label }
    },

    /**
     * Toggle sort dropdown visibility
     */
    toggleSortDropdown() {
      if (!this.nostoReady) {
        return
      }
      this.isSortDropdownOpen = !this.isSortDropdownOpen

      if (this.isSortDropdownOpen) {
        document.addEventListener('click', this.handleClickOutside)
      }
    },

    /**
     * Handle clicking outside of sort dropdown
     * @param {Event} event - Click event
     */
    handleClickOutside(event) {
      if (!this.$refs.sortDropdown) {
        return
      }

      if (!this.$refs.sortDropdown.contains(event.target)) {
        this.isSortDropdownOpen = false
        document.removeEventListener('click', this.handleClickOutside)
      }
    },

    /**
     * Handle sort option click
     * @param {Object} sort - Sort option object
     */
    handleSortByClick(sort) {
      if (!this.nostoReady) {
        return
      }

      const { id, label } = sort
      const action = id === this.sortOption.id ? 'remove' : 'add'

      this.$emit('choices-updated', {
        requestParam: `&sort=${id}`,
        action,
      })

      this.sortOption = action === 'add' ? { id, label } : { id: '', label: '' }
      this.isSortDropdownOpen = false
    },

    /**
     * Return empty or full circle icon based on active status of option.
     * @param {String} sortId - Id of sort option.
     */
    getSortIcon(sortId) {
      // Using getChoices instead of dataChoices
      if (this.getChoices.includes(sortId)) {
        return 'icon-circle-full'
      }

      return 'icon-circle-empty'
    },

    /**
     * Check if a facet has any active filters
     * @param {Object} facet - The facet to check
     * @returns {Boolean} - True if the facet has active filters
     */
    isFacetActive(facet) {
      if (!this.getChoices) {
        return false
      }

      // Get the field name from the facet
      const fieldName = facet.field
      let searchPattern = ''

      // Handle different field name formats
      if (fieldName.includes('customFields')) {
        // For custom fields like 'skus.customFields.colour'
        const fieldParts = fieldName.split('.')
        const lastPart = fieldParts[fieldParts.length - 1]
        searchPattern = `pf_skus__customFields__${lastPart}`
      } else {
        // For regular fields like 'size'
        searchPattern = `pf_${fieldName}`
      }

      // Check if this facet appears in active choices
      return this.getChoices.includes(searchPattern)
    },

    /**
     * Position facet dropdowns below their parent elements
     */
    applyFacetOffsets() {
      if (!this.$refs.facetAccordions) {
        return
      }

      const accordions = Array.isArray(this.$refs.facetAccordions)
        ? this.$refs.facetAccordions
        : [this.$refs.facetAccordions]

      accordions.forEach((filter) => {
        const child = filter.querySelector('.nosto-facet__content')
        if (!child) {
          return
        }

        child.style.position = 'absolute'
        child.style.left = '0'
        child.style.top = '100%'
        child.style.minWidth = '100%'
      })
    },

    /**
     * Get the count of selected values for a facet
     * @param {Object} facet - The facet to check
     * @returns {Number} - Number of selected values
     */
    getFacetSelectedCount(facet) {
      if (!facet?.data) {
        return 0
      }
      return facet.data.filter(value => value.selected).length
    },

    /**
     * Handle clicking outside of facet dropdowns
     * @param {Event} event - Click event
     */
    handleFacetClickOutside(event) {
      if (!this.$refs.facetComponents) {
        return
      }

      const components = Array.isArray(this.$refs.facetComponents)
        ? this.$refs.facetComponents
        : [this.$refs.facetComponents]

      let clickedOutside = true

      components.forEach(component => {
        if (component.$el) {
          // Use $el directly if it has the attribute, else querySelector
          const facetMobileEl = component.$el.hasAttribute('js-nosto-facet')
            ? component.$el
            : component.$el.querySelector('[js-nosto-facet="facetMobile"]')

          if (facetMobileEl && facetMobileEl.contains(event.target)) {
            clickedOutside = false
            this.activeFacetElement = facetMobileEl
          }
        }
      })

      if (clickedOutside && !event.target.closest('.nosto-facet__content') && !this.$store.state.index.screen.mobile) {
        this.removeControlsActiveClass()
        this.activeFacetId = null
      }
    },

    /**
     * Updates the active facet id when a facet is opened or closed.
     * @param {String|Number} facetId - The id of the facet being
     *   interacted with.
     * @param {Boolean} isActive - True if the facet is being opened,
     *   false if closed.
     */
    handleFacetActiveChange(facetId, isActive) {
      this.activeFacetId = isActive ? facetId : null
    },
  },
}
</script>

<style lang="scss">
@import './nosto-horizontal-filter';
</style>
